import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  User, 
  Wallet, 
  QrCode, 
  Sparkles, 
  Settings, 
  Mail, 
  MapPin 
} from "lucide-react";

export default function Podpora({ renderHeader, renderFooter }) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Věrnost – Podpora";
    
    // Dynamically update or create meta description tag
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Potřebujete poradit s aplikací Věrnost? Kontaktujte naši podporu.');
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFDFD] text-[#111111] font-sans antialiased selection:bg-[#FF5A00]/10 selection:text-[#FF5A00]">
      {renderHeader()}

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-left">
        {/* Document Title Header */}
        <div className="border-b-2 border-[#FF5A00]/20 pb-8 mb-10">
          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-neutral-900 leading-tight mb-4" data-testid="support-heading">
            Podpora Věrnost
          </h1>
          <p className="text-xl font-medium text-[#FF5A00]" data-testid="support-intro">
            Potřebujete poradit s aplikací Věrnost?
          </p>
        </div>

        {/* Content Section */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          {/* Main Info */}
          <div className="md:col-span-7 space-y-6">
            <p className="text-base sm:text-lg text-neutral-700 leading-relaxed font-medium" data-testid="support-text">
              Pokud máte problém s aplikací, svým účtem, věrnostní kartou, kupónem nebo předplatným Premium, rádi vám pomůžeme.
            </p>

            <div className="mt-8 border-t border-neutral-100 pt-8">
              <h2 className="font-heading font-extrabold text-xl text-neutral-900 mb-4" data-testid="support-categories-heading">
                S čím vám můžeme pomoci?
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-neutral-50 rounded-2xl border border-neutral-100 flex items-start space-x-3" data-testid="support-category-auth">
                  <User className="w-5 h-5 text-[#FF5A00] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-extrabold text-neutral-900 mb-1">Účet a přihlášení</h4>
                    <p className="text-xs text-neutral-500 font-medium">Správa profilu a řešení přístupových SMS kódů.</p>
                  </div>
                </div>
                <div className="p-4 bg-neutral-50 rounded-2xl border border-neutral-100 flex items-start space-x-3" data-testid="support-category-cards">
                  <Wallet className="w-5 h-5 text-[#FF5A00] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-extrabold text-neutral-900 mb-1">Věrnostní karty a návštěvy</h4>
                    <p className="text-xs text-neutral-500 font-medium">Sbírání razítek, historie a uplatnění odměn.</p>
                  </div>
                </div>
                <div className="p-4 bg-neutral-50 rounded-2xl border border-neutral-100 flex items-start space-x-3" data-testid="support-category-coupons">
                  <QrCode className="w-5 h-5 text-[#FF5A00] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-extrabold text-neutral-900 mb-1">Kupóny</h4>
                    <p className="text-xs text-neutral-500 font-medium">Získávání, aktivace a správa kupónů.</p>
                  </div>
                </div>
                <div className="p-4 bg-neutral-50 rounded-2xl border border-neutral-100 flex items-start space-x-3" data-testid="support-category-premium">
                  <Sparkles className="w-5 h-5 text-[#FF5A00] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-extrabold text-neutral-900 mb-1">Věrnost Premium</h4>
                    <p className="text-xs text-neutral-500 font-medium">Předplatné, Premium návštěvy a výhody.</p>
                  </div>
                </div>
                <div className="p-4 bg-neutral-50 rounded-2xl border border-[#FF5A00]/20 flex items-start space-x-3 sm:col-span-2 shadow-sm" data-testid="support-category-tech">
                  <Settings className="w-5 h-5 text-[#FF5A00] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-extrabold text-neutral-900 mb-1">Technické problémy</h4>
                    <p className="text-xs text-neutral-500 font-medium">Řešení chyb v aplikaci, sítě nebo zobrazení.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Box */}
          <div className="md:col-span-5">
            <div className="bg-neutral-50 rounded-3xl p-6 sm:p-8 border border-neutral-100 text-left space-y-6 shadow-sm" data-testid="support-contact-card">
              <h3 className="font-heading font-extrabold text-xl text-neutral-900 mb-2">
                Kontaktujte nás
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3 text-neutral-700">
                  <a 
                    href="mailto:podpora@vernostapp.cz"
                    className="w-10 h-10 rounded-full bg-[#FF5A00]/10 flex items-center justify-center text-[#FF5A00] hover:bg-[#FF5A00] hover:text-white transition-colors duration-200"
                    data-testid="support-email-icon-link"
                  >
                    <Mail className="w-5 h-5" />
                  </a>
                  <div>
                    <p className="text-xs text-neutral-400 font-bold uppercase tracking-wider">E-mail</p>
                    <a 
                      href="mailto:podpora@vernostapp.cz"
                      className="text-base font-extrabold text-neutral-800 hover:text-[#FF5A00] transition-colors"
                      data-testid="support-email-link"
                    >
                      podpora@vernostapp.cz
                    </a>
                  </div>
                </div>

                <div className="flex items-center space-x-3 text-neutral-700">
                  <div className="w-10 h-10 rounded-full bg-neutral-200/50 flex items-center justify-center text-neutral-500">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-400 font-bold uppercase tracking-wider">Lokalita</p>
                    <p className="text-base font-extrabold text-neutral-800">Brno, Česká republika</p>
                  </div>
                </div>
              </div>

              <p className="text-sm font-medium text-neutral-500 italic border-t border-neutral-150 pt-4 leading-relaxed">
                Na váš dotaz odpovíme co nejdříve.
              </p>
            </div>
          </div>
        </div>
      </main>

      {renderFooter()}
    </div>
  );
}
